import { useQuery } from '@tanstack/react-query';
import api from './authService';

// Query Keys
export const vendorAnalyticsKeys = {
  all: ['vendor-analytics'],
  overview: (period, channel) => [...vendorAnalyticsKeys.all, 'overview', period, channel || 'all'],
  performance: (period, channel) => [
    ...vendorAnalyticsKeys.all,
    'performance',
    period,
    channel || 'all',
  ],
  earnings: (period, channel) => [...vendorAnalyticsKeys.all, 'earnings', period, channel || 'all'],
  reports: (type, period, channel) => [
    ...vendorAnalyticsKeys.all,
    'reports',
    type,
    period,
    channel || 'all',
  ],
  insights: (period) => [...vendorAnalyticsKeys.all, 'insights', period],
};

// API Functions
const vendorAnalyticsApi = {
  getOverview: async (period = 'month', channel = 'all') => {
    const response = await api.get(`/vendor/analytics`, {
      params: { period, channel },
    });
    return response.data;
  },

  getPerformance: async (period = 'month', channel = 'all') => {
    const response = await api.get(`/vendor/analytics/performance`, {
      params: { period, channel },
    });
    return response.data;
  },

  getEarnings: async (period = 'month', channel = 'all') => {
    const response = await api.get(`/vendor/analytics/earnings`, {
      params: { period, channel },
    });
    return response.data;
  },

  getReports: async (type = 'bookings', period = 'month', channel = 'all') => {
    const response = await api.get(`/vendor/reports`, {
      params: { type, period, channel },
    });
    return response.data;
  },

  exportReportExcel: async ({ period = 'month', channel = 'all' } = {}) => {
    const response = await api.get(`/vendor/reports/export`, {
      params: { period, channel },
      responseType: 'blob',
    });
    return response;
  },

  getInsights: async (period = 'month') => {
    const response = await api.get(`/vendor/analytics/insights`, {
      params: { period },
    });
    return response.data;
  },
};

/**
 * @param {string} period
 * @param {string|object} channelOrOptions - channel string, or legacy options object
 * @param {object} [options]
 */
export const useVendorAnalytics = (period = 'month', channelOrOptions = 'all', options = {}) => {
  const channel =
    typeof channelOrOptions === 'string' ? channelOrOptions : channelOrOptions?.channel || 'all';
  const queryOptions = typeof channelOrOptions === 'object' ? channelOrOptions : options;

  return useQuery({
    queryKey: vendorAnalyticsKeys.overview(period, channel),
    queryFn: () => vendorAnalyticsApi.getOverview(period, channel),
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
    ...queryOptions,
  });
};

export const useVendorPerformance = (period = 'month', channelOrOptions = 'all', options = {}) => {
  const channel =
    typeof channelOrOptions === 'string' ? channelOrOptions : channelOrOptions?.channel || 'all';
  const queryOptions = typeof channelOrOptions === 'object' ? channelOrOptions : options;

  return useQuery({
    queryKey: vendorAnalyticsKeys.performance(period, channel),
    queryFn: () => vendorAnalyticsApi.getPerformance(period, channel),
    staleTime: 5 * 60 * 1000,
    ...queryOptions,
  });
};

export const useVendorEarnings = (period = 'month', channelOrOptions = 'all', options = {}) => {
  const channel =
    typeof channelOrOptions === 'string' ? channelOrOptions : channelOrOptions?.channel || 'all';
  const queryOptions = typeof channelOrOptions === 'object' ? channelOrOptions : options;

  return useQuery({
    queryKey: vendorAnalyticsKeys.earnings(period, channel),
    queryFn: () => vendorAnalyticsApi.getEarnings(period, channel),
    staleTime: 5 * 60 * 1000,
    ...queryOptions,
  });
};

export const useVendorReports = (
  type = 'bookings',
  period = 'month',
  channelOrOptions = 'all',
  options = {}
) => {
  const channel =
    typeof channelOrOptions === 'string' ? channelOrOptions : channelOrOptions?.channel || 'all';
  const queryOptions = typeof channelOrOptions === 'object' ? channelOrOptions : options;

  return useQuery({
    queryKey: vendorAnalyticsKeys.reports(type, period, channel),
    queryFn: () => vendorAnalyticsApi.getReports(type, period, channel),
    staleTime: 5 * 60 * 1000,
    ...queryOptions,
  });
};

export const useVendorInsights = (period = 'month', options = {}) => {
  return useQuery({
    queryKey: vendorAnalyticsKeys.insights(period),
    queryFn: () => vendorAnalyticsApi.getInsights(period),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export default vendorAnalyticsApi;
